import { Form } from "react-aria-components";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Button from "../components/Button";
import Input from "../components/Input";
import { CreateUser } from "../types";
import useCreateUser from "../hooks/useCreateUser";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState, useRef } from "react";
import { debounce } from "lodash";
import useAutocomplete from "@/hooks/useAutocomplete";
import * as Sentry from "@sentry/react";
import Page from "@/components/Page";
import { trackEvent } from "@/lib/amplitude";

export default function FinishRegistration() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [term, setTerm] = useState<string>();
  const [showDialog, setShowDialog] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const hasTrackedPageView = useRef(false);
  const placeRef = window.localStorage.getItem("placeRef");

  // Track page view
  useEffect(() => {
    if (!hasTrackedPageView.current) {
      hasTrackedPageView.current = true;
      trackEvent("registration_page_viewed", { placeRef });
    }
  }, []);

  const { mutate, isPending } = useCreateUser({
    onSuccess: () => {
      trackEvent("registration_completed", { placeRef });
      navigate("/upload");
    },
    onError: (error) => {
      console.error(error);
      Sentry.captureException(error);
      alert(t("upload-video.alert-error"));
    },
  });

  const { register, setValue, handleSubmit } = useForm<CreateUser>();

  const updateTerm = useCallback(
    debounce((term: string) => setTerm(term), 1000),
    [],
  );

  const { data, isLoading } = useAutocomplete({ term: term });

  const onSubmit: SubmitHandler<CreateUser> = async ({ place, name }) => {
    if (place == null) {
      alert(t("finish-registration.alert-city-validation"));
      return;
    }

    try {
      mutate({ place, name });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    if (!!data?.length) {
      setShowDialog(true);
    }
  }, [data]);

  return (
    <>
      <Page title={t("finish-registration.title")} lngChange back>
        <h2 className="mt-7 w-full text-center">{t("finish-registration.subtitle")}</h2>

        <Form
          onSubmit={handleSubmit(onSubmit)}
          id="form"
          className="mt-7 flex grow  flex-col justify-between"
        >
          <div className="space-y-4 text-black">
            <Input
              disabled={isPending}
              placeholder={t("finish-registration.placeholder.name")}
              required
              {...register("name")}
            />

            <Input
              required
              value={inputValue}
              placeholder={t("finish-registration.placeholder.city")}
              onChange={(e) => {
                setInputValue(e.target.value);
                updateTerm(e.target.value);
              }}
            />

            {isLoading && <p className="text-white">{t("common.loading")}...</p>}

            {!!showDialog && (
              <ul className="overflow-hidden rounded-2xl border border-medium-gray bg-white shadow-md">
                {data?.map((item) => {
                  return (
                    <li
                      key={item.position.latitude + item.position.longitude}
                      className="cursor-pointer text-black hover:bg-light-gray/20"
                    >
                      <button
                        onClick={() => {
                          setShowDialog(false);
                          setValue("place", item);
                          setInputValue(item.longName);
                          trackEvent("city_selected", {
                            placeRef,
                            cityName: item.name,
                            cityLongName: item.longName,
                          });
                        }}
                        type="button"
                        className="w-full px-4 py-3 text-left"
                      >
                        {item.longName}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <Button isPending={isPending} form="form" variant="secondary" type="submit">
            {t("finish-registration.button")}
          </Button>
        </Form>
      </Page>
    </>
  );
}
